import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export { API };

const api = axios.create({
  withCredentials: true
});

// === Idempotency-Key auto-injection ===
// Pour les endpoints critiques (ventes, paiements, remboursements,
// paiements fournisseurs, voyages, devis, bons de commande, ajustements ledger),
// on genere un UUID v4 et l'ajoute en header. Empeche les doubles operations
// en cas de double-clic, retry reseau ou connexion instable.
const IDEMPOTENT_PATHS = [
  '/api/ventes/',
  '/api/paiements/',
  '/api/remboursements/',
  '/api/paiements-fournisseurs/',
  '/api/voyages/',
  '/api/devis/',
  '/api/bons-commande/',
  '/api/supplier-ledger/adjustment',
  '/api/supplier-ledger/payment',
];

function _uuidv4() {
  // Crypto API (navigateur moderne)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback (pas un vrai uuid mais suffisant comme cle d'idempotence)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

api.interceptors.request.use((config) => {
  if ((config.method || '').toLowerCase() === 'post') {
    // Match exact sur /api/X/ (et pas sur /api/X/Y/Z, ce qui exclut /approve, /vendre, etc.)
    const url = config.url || '';
    const matchesIdempotent = IDEMPOTENT_PATHS.some((p) => {
      const idx = url.indexOf(p);
      if (idx === -1) return false;
      const tail = url.slice(idx + p.length);
      // tail vide ou query-string seulement -> c'est bien la racine du POST
      return tail === '' || tail.startsWith('?');
    });
    if (matchesIdempotent && !(config.headers && config.headers['Idempotency-Key'])) {
      config.headers = { ...(config.headers || {}), 'Idempotency-Key': _uuidv4() };
    }
  }
  return config;
});

// Global response interceptor with auto-refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Network error (offline / serveur injoignable)
    if (!error.response) {
      toast.error('Connexion impossible. Verifiez votre connexion internet.');
      return Promise.reject(error);
    }

    const { status, config } = error;
    const originalRequest = config;

    // 401 - try refresh token before redirecting
    if (status === 401 && !originalRequest._retry && !config.url?.includes('/auth/login') && !config.url?.includes('/auth/me') && !config.url?.includes('/auth/refresh')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await api.post(`${API}/auth/refresh`);
        processQueue(null);
        isRefreshing = false;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        isRefreshing = false;
        // Récupère le message précis du backend (session unique : "un autre appareil...")
        const detail = refreshError?.response?.data?.detail || error?.response?.data?.detail;
        const msg = (detail && /autre appareil/i.test(detail))
          ? detail
          : 'Session expiree. Veuillez vous reconnecter.';
        toast.error(msg);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // 401 on /auth/me after refresh failed
    if (status === 401 && config.url?.includes('/auth/me')) {
      return Promise.reject(error);
    }

    // 500 - erreur serveur
    if (status >= 500) {
      toast.error('Erreur serveur. Veuillez reessayer plus tard.');
    }

    return Promise.reject(error);
  }
);


// Auth extras
export const authAPI = {
  sendOtp: (email) => api.post(`${API}/auth/send-otp`, { email }),
  verifyOtp: (email, code) => api.post(`${API}/auth/verify-otp`, { email, code }),
  changePassword: (old_password, new_password) => api.post(`${API}/auth/change-password`, { old_password, new_password }),
  getAgentsPasswords: () => api.get(`${API}/auth/agents-passwords`),
  resetPassword: (telephone, new_password) => api.post(`${API}/auth/reset-password`, { telephone, new_password }),
  changePhone: (old_telephone, new_telephone) => api.post(`${API}/auth/change-phone`, { old_telephone, new_telephone })
};

// Clients
export const clientsAPI = {
  getAll: (params) => api.get(`${API}/clients/`, { params }),
  getOne: (id) => api.get(`${API}/clients/${id}`),
  create: (data) => api.post(`${API}/clients/`, data),
  update: (id, data) => api.put(`${API}/clients/${id}`, data),
  delete: (id) => api.delete(`${API}/clients/${id}`),
  uploadLogo: (id, formData) => api.post(`${API}/clients/${id}/upload-logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
};

// Produits
export const produitsAPI = {
  getAll: (params) => api.get(`${API}/produits/`, { params }),
  getOne: (id) => api.get(`${API}/produits/${id}`),
  create: (data) => api.post(`${API}/produits/`, data),
  update: (id, data) => api.put(`${API}/produits/${id}`, data),
  delete: (id) => api.delete(`${API}/produits/${id}`)
};

// Fournisseurs
export const fournisseursAPI = {
  getAll: (params) => api.get(`${API}/fournisseurs/`, { params }),
  getOne: (id) => api.get(`${API}/fournisseurs/${id}`),
  create: (data) => api.post(`${API}/fournisseurs/`, data),
  update: (id, data) => api.put(`${API}/fournisseurs/${id}`, data),
  delete: (id) => api.delete(`${API}/fournisseurs/${id}`),
  getStats: (id) => api.get(`${API}/fournisseurs/${id}/stats`),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${API}/fournisseurs/${id}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Supplier Ledger (international append-only journal)
export const supplierLedgerAPI = {
  getSituation: (supplierId, params) =>
    api.get(`${API}/supplier-ledger/situation/${supplierId}`, { params }),
  getTransactions: (supplierId, params) =>
    api.get(`${API}/supplier-ledger/transactions/${supplierId}`, { params }),
  getVoyageGroup: (code, params) =>
    api.get(`${API}/supplier-ledger/voyage-group/${code}`, { params }),
  createAdjustment: (data) =>
    api.post(`${API}/supplier-ledger/adjustment`, data),
  createPayment: (data) =>
    api.post(`${API}/supplier-ledger/payment`, data),
  exportPaymentsCsvUrl: (supplierId) =>
    `${API}/supplier-ledger/payments/${supplierId}/export-csv`,
};

// Supplier Receipts (CQRS projection, read-only, Phase 1)
export const supplierReceiptsAPI = {
  getList: (supplierId) =>
    api.get(`${API}/supplier-receipts/list/${supplierId}`),
  getTimeline: (receiptKey) =>
    api.get(`${API}/supplier-receipts/timeline/${encodeURIComponent(receiptKey)}`),
  getReconciliation: (supplierId) =>
    api.get(`${API}/supplier-receipts/reconciliation/${supplierId}`),
  getHealthDashboard: () =>
    api.get(`${API}/supplier-receipts/health-dashboard`),
  // Phase 2 Lot A — Allocation FIFO
  previewAllocation: (data) =>
    api.post(`${API}/supplier-receipts/preview-allocation`, data),
  applyAllocation: (data, idempotencyKey) =>
    api.post(`${API}/supplier-receipts/apply-allocation`, data, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    }),
  // Phase 2 Lot B — Ajustement EXCEPTION (4-eyes)
  applyExceptionAdjustment: (data, idempotencyKey) =>
    api.post(`${API}/supplier-receipts/exception-adjustment`, data, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    }),
  // Phase 3 — PDF récapitulatif (régénéré à la demande, watermark "non comptable")
  downloadPdfUrl: (receiptKey) =>
    `${API}/supplier-receipts/pdf/${encodeURIComponent(receiptKey)}`,
};

// iter89 P1 — Queue collaborative billets_en_attente
export const billetsQueueAPI = {
  list: (params = {}) => api.get(`${API}/billets-en-attente/list`, { params }),
  claim: (billetId) => api.post(`${API}/billets-en-attente/${billetId}/claim`),
  release: (billetId) => api.post(`${API}/billets-en-attente/${billetId}/release`),
  heartbeat: (billetId) => api.post(`${API}/billets-en-attente/${billetId}/heartbeat`),
  discard: (billetId, reason) => api.post(`${API}/billets-en-attente/${billetId}/discard`, { reason }),
  finalize: (billetId, venteId) => api.post(`${API}/billets-en-attente/${billetId}/finalize`, { vente_id: venteId }),
};

// Ventes
export const ventesAPI = {
  getAll: (params) => api.get(`${API}/ventes/`, { params }),
  getOne: (id) => api.get(`${API}/ventes/${id}`),
  create: (data) => api.post(`${API}/ventes/`, data),
  updateStatus: (id, statut, extra = {}) => api.put(`${API}/ventes/${id}/status`, { statut, ...extra }),
  updateProduitPrix: (venteId, produitIndex, data) => api.patch(`${API}/ventes/${venteId}/produit/${produitIndex}`, data),
  delete: (id) => api.delete(`${API}/ventes/${id}`),
  recherche: (params) => api.get(`${API}/ventes/recherche`, { params }),
  migrateRoom: (venteId, data) => api.post(`${API}/ventes/${venteId}/migrate-room`, data)
};

// Paiements
export const paiementsAPI = {
  getAll: (params) => api.get(`${API}/paiements/`, { params }),
  getOne: (id) => api.get(`${API}/paiements/${id}`),
  getVenteDetails: (venteId) => api.get(`${API}/paiements/vente-details/${venteId}`),
  create: (data) => api.post(`${API}/paiements/`, data)
};

// Remboursements
export const remboursementsAPI = {
  getAll: (params) => api.get(`${API}/remboursements/`, { params }),
  create: (data) => api.post(`${API}/remboursements/`, data),
  approve: (id) => api.post(`${API}/remboursements/${id}/approve`, {}),
  reject: (id) => api.post(`${API}/remboursements/${id}/reject`, {})
};

// Rapports
export const rapportsAPI = {
  getDashboard: (voyage_filter = 'all') => api.get(`${API}/rapports/dashboard`, { params: { voyage_filter } }),
  getChiffreAffaires: (periode, voyage_filter = 'all') => api.get(`${API}/rapports/chiffre-affaires`, { params: { periode, voyage_filter } }),
  getEncaissements: () => api.get(`${API}/rapports/encaissements`),
  getImpayes: () => api.get(`${API}/rapports/impayes`),
  getRemboursementsStats: () => api.get(`${API}/rapports/remboursements-stats`),
  getTopProduits: (voyage_filter = 'all') => api.get(`${API}/rapports/top-produits`, { params: { voyage_filter } }),
  getTopClients: (voyage_filter = 'all') => api.get(`${API}/rapports/top-clients`, { params: { voyage_filter } }),
  getPaiementsFournisseursRapport: (voyage_filter = 'all') => api.get(`${API}/rapports/paiements-fournisseurs-rapport`, { params: { voyage_filter } }),
  getVentesPeriode: (start, end) => api.get(`${API}/rapports/ventes-par-periode`, { params: { start_date: start, end_date: end } }),
  getPerformanceAgents: (voyage_filter = 'all') => api.get(`${API}/rapports/performance-agents`, { params: { voyage_filter } }),
  getPerformanceFournisseurs: () => api.get(`${API}/rapports/performance-fournisseurs`),
  exportCSV: (type) => `${API}/rapports/export-csv/${type}`,
  rechercheHistorique: (params) => api.get(`${API}/rapports/recherche-historique`, { params }),
  archiver: (data) => api.post(`${API}/rapports/archiver`, data, { responseType: 'blob' }),
  getCommissions: (params) => api.get(`${API}/rapports/commissions`, { params }),
  getCaFournisseurs: (params) => api.get(`${API}/rapports/ca-fournisseurs`, { params }),
  getDetailVentesFournisseur: (params) => api.get(`${API}/rapports/detail-ventes-fournisseur`, { params }),
  getHistoriqueClient: (params) => api.get(`${API}/rapports/historique-client`, { params })
};

// Audit Logs (System Audit - chef only)
export const auditLogsAPI = {
  list: (params) => api.get(`${API}/audit-logs/`, { params }),
  stats: () => api.get(`${API}/audit-logs/stats`),
  exportCsv: (params) => `${API}/audit-logs/export?${new URLSearchParams(params || {}).toString()}`,
};

// PDF
export const pdfAPI = {
  getRecuPaiement: (id, lang = 'fr') => `${API}/pdf/recu-paiement/${id}?lang=${lang}`,
  getRecuRemboursement: (id, lang = 'fr') => `${API}/pdf/recu-remboursement/${id}?lang=${lang}`,
  getFactureVente: (id, lang = 'fr') => `${API}/pdf/vente/${id}?lang=${lang}`
};

// Agents
export const agentsAPI = {
  getAll: () => api.get(`${API}/agents/`),
  updatePermissions: (id, permissions) => api.put(`${API}/agents/${id}/permissions`, permissions),
  delete: (id) => api.delete(`${API}/agents/${id}`)
};

// Agence Settings
export const agenceAPI = {
  getSettings: () => api.get(`${API}/agence/settings`),
  updateSettings: (data) => api.put(`${API}/agence/settings`, data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${API}/agence/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Paiements Fournisseurs
export const paiementsFournisseursAPI = {
  getAll: (params) => api.get(`${API}/paiements-fournisseurs/`, { params }),
  create: (data) => api.post(`${API}/paiements-fournisseurs/`, data),
  update: (id, data) => api.put(`${API}/paiements-fournisseurs/${id}`, data),
  delete: (id) => api.delete(`${API}/paiements-fournisseurs/${id}`),
  getResume: () => api.get(`${API}/paiements-fournisseurs/resume-fournisseurs`)
};

// Billetterie Fournisseur
export const billetterieAPI = {
  getFacture: (params) => api.get(`${API}/billetterie/factures`, { params }),
  createPaiement: (data) => api.post(`${API}/billetterie/paiements`, data),
  deletePaiement: (id) => api.delete(`${API}/billetterie/paiements/${id}`),
  uploadJustificatif: (paiementId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${API}/billetterie/paiements/${paiementId}/justificatif`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getHistorique: (fournisseurId) => api.get(`${API}/billetterie/historique`, { params: { fournisseur_id: fournisseurId } })
};

// Avoirs
export const avoirsAPI = {
  getListe: (params) => api.get(`${API}/avoirs/liste`, { params }),
  demandeReglement: (data) => api.post(`${API}/avoirs/demande-reglement`, data),
  approve: (id) => api.post(`${API}/avoirs/${id}/approve`),
  reject: (id) => api.post(`${API}/avoirs/${id}/reject`)
};

// Email History
export const emailAPI = {
  getHistorique: (params) => api.get(`${API}/email/historique`, { params })
};

// Devis
export const devisAPI = {
  getAll: (params) => api.get(`${API}/devis/liste`, { params }),
  getOne: (id) => api.get(`${API}/devis/${id}`),
  create: (data) => api.post(`${API}/devis/`, data),
  update: (id, data) => api.put(`${API}/devis/${id}`, data),
  delete: (id) => api.delete(`${API}/devis/${id}`),
  transformer: (id, data) => api.post(`${API}/devis/${id}/transformer`, data),
  createRappel: (id, data) => api.post(`${API}/devis/${id}/rappel`, data),
  getRappel: (id) => api.get(`${API}/devis/${id}/rappel`),
  deleteRappel: (id) => api.delete(`${API}/devis/${id}/rappel`)
};

// Chat & Notifications
export const chatAPI = {
  getMessages: (params) => api.get(`${API}/chat/messages`, { params }),
  sendMessage: (data) => api.post(`${API}/chat/messages`, data),
  getNotifications: (params) => api.get(`${API}/chat/notifications`, { params }),
  getUnreadCount: () => api.get(`${API}/chat/notifications/count`),
  markRead: (ids) => api.post(`${API}/chat/notifications/read`, { ids }),
  markAllRead: () => api.post(`${API}/chat/notifications/read-all`)
};

// Voyages Organisés
export const voyagesAPI = {
  getAll: (params) => api.get(`${API}/voyages/`, { params }),
  getDisponibles: () => api.get(`${API}/voyages/disponibles`),
  getOne: (id) => api.get(`${API}/voyages/${id}`),
  create: (data) => api.post(`${API}/voyages/`, data),
  update: (id, data) => api.put(`${API}/voyages/${id}`, data),
  delete: (id) => api.delete(`${API}/voyages/${id}`),
  toggleStatut: (id) => api.put(`${API}/voyages/${id}/statut`, {}),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${API}/voyages/${id}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  vendrPlace: (id, nombre = 1) => api.post(`${API}/voyages/${id}/vendre?nombre=${nombre}`, {}),
  libererPlace: (id, nombre = 1) => api.post(`${API}/voyages/${id}/liberer?nombre=${nombre}`, {}),
  recomputePlaces: (id) => api.post(`${API}/voyages/${id}/recompute-places`, {}),
  exportExcel: (voyage_ids) => api.post(`${API}/voyages/export-excel`, { voyage_ids: voyage_ids || null }, { responseType: 'blob' }),
  getParticipants: (id) => api.get(`${API}/voyages/${id}/participants`),
  getManifeste: (id) => api.get(`${API}/voyages/${id}/manifeste`),
  getRapport: (id) => api.get(`${API}/voyages/${id}/rapport`),
  getRapportFinancier: (id) => api.get(`${API}/voyages/${id}/rapport-financier`),
  getRapportGlobalMesVoyages: () => api.get(`${API}/voyages/rapport-global/mes-voyages`),
  cloturerSaisonPreview: (data) => api.post(`${API}/voyages/cloturer-saison/preview`, data),
  cloturerSaisonExecute: (data) => api.post(`${API}/voyages/cloturer-saison/execute`, data, { responseType: 'blob' }),
  dupliquer: (id, nouveau_nom) => api.post(`${API}/voyages/${id}/dupliquer`, null, { params: nouveau_nom ? { nouveau_nom } : {} }),
  importExcel: (formData) => api.post(`${API}/voyages/import-excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
};

// Bons de Commande
export const bonsCommandeAPI = {
  getAll: (params) => api.get(`${API}/bons-commande/`, { params }),
  getOne: (id) => api.get(`${API}/bons-commande/${id}`),
  create: (data) => api.post(`${API}/bons-commande/`, data),
  update: (id, data) => api.put(`${API}/bons-commande/${id}`, data),
  delete: (id) => api.delete(`${API}/bons-commande/${id}`)
};

// Import Amadeus
export const amadeusAPI = {
  scan: (data) => api.post(`${API}/amadeus/scan`, data || {}),
  importBooking: (data) => api.post(`${API}/amadeus/import`, data),
  searchClients: (q) => api.get(`${API}/amadeus/clients-search`, { params: { q } }),
  listFournisseurs: () => api.get(`${API}/amadeus/fournisseurs-list`)
};

// Sources d'importation (Amadeus, Via Nostra, etc.)
export const importSourcesAPI = {
  list: () => api.get(`${API}/import-sources/`),
  create: (data) => api.post(`${API}/import-sources/`, data),
  update: (id, data) => api.put(`${API}/import-sources/${id}`, data),
  delete: (id) => api.delete(`${API}/import-sources/${id}`),
  testCredentials: (data) => api.post(`${API}/import-sources/test-credentials`, data)
};

// Frais de Voyage (additionnels : billet guide, frais mission, etc.)
export const fraisVoyageAPI = {
  list: (voyageId) => api.get(`${API}/frais-voyage/`, { params: voyageId ? { voyage_id: voyageId } : {} }),
  create: (data) => api.post(`${API}/frais-voyage/`, data),
  update: (id, data) => api.put(`${API}/frais-voyage/${id}`, data),
  delete: (id) => api.delete(`${API}/frais-voyage/${id}`)
};

export default api;
